import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import { entityService } from "../../services/entityService";

const log = createFileLogger('ENTITY_TYPE_CREATE');

export const entityTypeCreateCommand: SelfContainedCommand = {
  id: 'entity_type_create',
  description: `Create a new custom entity type definition.

This allows you to define new entity types that can be created and managed in River.

**PARAMETERS:**
- typeName: Unique name for the entity type (e.g., "StoryCharacter", "Recipe")
- displayName: Human-readable name (e.g., "Story Character", "Recipe")
- component: React component name to render this type (e.g., "StoryCharacterView")
- icon: Phosphor icon name (e.g., "User", "CookingPot")
- category: Category for organization (optional)

**EXAMPLE:**
{
  "typeName": "Recipe",
  "displayName": "Recipe",
  "component": "RecipeView",
  "icon": "CookingPot",
  "category": "creative"
}

**RETURNS:** EntityTypeDefinition entity
`,
  autonomyLevel: 'ask',
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'typeName',
        type: 'string',
        description: 'Unique entity type name (PascalCase, e.g., StoryCharacter)',
        required: true,
        validator: (value: string) => {
          if (!value || typeof value !== 'string') {
            return 'typeName is required';
          }
          if (!/^[A-Z][a-zA-Z0-9]*$/.test(value)) {
            return 'typeName must be PascalCase (e.g., StoryCharacter, Recipe)';
          }
          return null;
        }
      },
      {
        name: 'displayName',
        type: 'string',
        description: 'Human-readable name for the entity type',
        required: true
      },
      {
        name: 'component',
        type: 'string',
        description: 'React component name to render entities of this type',
        required: true
      },
      {
        name: 'icon',
        type: 'string',
        description: 'Phosphor icon name (e.g., User, Star, Book)',
        required: false
      },
      {
        name: 'category',
        type: 'string',
        description: 'Category for organization (e.g., creative, business, personal)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          typeName: 'Recipe',
          displayName: 'Recipe',
          component: 'RecipeView',
          icon: 'CookingPot',
          category: 'creative'
        },
        expectedOutput: createSuccessResult('Entity type "Recipe" created successfully', {}),
        description: 'Create a custom Recipe entity type',
        context: 'When you need a new entity type for organizing recipes'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { typeName, displayName, component, icon, category } = params;
    
    log.info('[entity_type_create] Creating entity type:', { typeName, displayName });
    
    try {
      // Validate required fields
      if (!typeName || !displayName || !component) {
        return createErrorResult(
          'Missing required fields',
          'typeName, displayName, and component are required',
          'validation',
          { data: params }
        );
      }
      
      // Check if entity type already exists
      const existing = await entityService.queryEntities({
        entityType: 'EntityTypeDefinition',
        'skeleton.typeName': typeName
      });
      
      if (existing && existing.length > 0) {
        return createErrorResult(
          'Entity type already exists',
          `An entity type with name "${typeName}" already exists`,
          'validation',
          { data: { typeName, existingId: existing[0]._id } }
        );
      }
      
      // Create EntityTypeDefinition entity
      const newEntityType = await entityService.create({
        entityType: 'EntityTypeDefinition',
        name: displayName,
        icon: icon || 'Star',
        skeleton: {
          '@type': 'EntityTypeDefinition',
          typeName,
          displayName,
          viewConfig: {
            appType: 'entity-view',
            component,
            standalone: false
          },
          icon: {
            type: 'phosphor',
            phosphorIcon: icon || 'Star',
            phosphorWeight: 'duotone'
          },
          category: category || 'custom',
          canCreateInFolder: true,
          hiddenFromLists: false,
          schema: {},
          creationCommand: `${typeName.toLowerCase()}_create`,
          publishing: {
            isPublished: false,
            isDraft: true,
            isVerified: false,
            visibility: 'private'
          }
        }
      });
      
      log.info('[entity_type_create] Created entity type:', newEntityType._id);
      
      // Register the new type in the live registry so it's usable immediately
      try {
        const { entityTypeRegistry } = await import('../../services/entityTypeRegistry');
        entityTypeRegistry.registerDefinition(newEntityType as Parameters<typeof entityTypeRegistry.registerDefinition>[0]);
        log.info('[entity_type_create] Registered new type in registry');
      } catch (reloadError) {
        log.warn('[entity_type_create] Failed to register new type in registry:', reloadError);
      }
      
      return createSuccessResult(
        `Entity type "${displayName}" created successfully`,
        {
          entityTypeDefinition: {
            id: newEntityType._id || newEntityType.id,
            typeName,
            displayName,
            component
          }
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityService.create',
            affectedEntities: [newEntityType._id || newEntityType.id]
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[entity_type_create] Error:', error);
      return createErrorResult(
        'Entity type creation failed',
        error.message || 'Unknown error',
        'execution',
        {
          data: params,
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
